import users from '../actions/users';
import { appData } from './common';
import SMILHandler from './SMILHandler';
import { CACHE_CONFIG_TYPES } from '@intigral/constant';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { forceLogout } from '../helpers/common';
import user from './user';
import { container } from '@intigral/domain/lib/DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { ErrorType } from '@intigral/utils';

/**
 * Classifies a network-level fetch error into one of the
 * defined ErrorType values based on the error name and message.
 * @param {Error} error
 * @returns {string} ErrorType value
 */
const classifyNetworkError = (error) => {
    if (error.name === 'AbortError') {
        return ErrorType.REQUEST_TIMEOUT;
    }
    const msg = (error.message ?? '').toLowerCase();
    if (msg.includes('ssl') || msg.includes('tls') || msg.includes('certificate') || msg.includes('cert_')) {
        return ErrorType.SSL_TLS_HANDSHAKE_FAILURE;
    }
    if (
        msg.includes('getaddrinfo') ||
        msg.includes('name not resolved') ||
        msg.includes('enotfound') ||
        msg.includes('dns')
    ) {
        return ErrorType.DNS_LOOKUP_FAILURE;
    }
    return ErrorType.OTHER_NETWORK_FAILURE;
};

const apis = {
    /**
     * API to call data
     * @param url
     * @param data
     * @param method
     * @param headers
     * @param requestOptions
     * @returns {Promise<unknown>}
     */
    callApi: (
        url,
        data = null,
        method = 'GET',
        headers = {},
        requestOptions = { responseJson: true },
        responseStatus = false,
        track
    ) => {
        if (headers['content-type'] === undefined) {
            headers['content-type'] = 'application/json';
        }

        if (headers['Authorization'] !== undefined || headers['authorization'] !== undefined) {
            // headers['identityToken'] = user.getIdentityToken();
        }
        if (headers['identityToken'] !== undefined) {
            headers['identityToken'] = user.getIdentityToken();
        }

        /** Auto login using Cookie */
        if (
            typeof headers['cookieIdentityToken'] !== 'undefined' &&
            headers['cookieIdentityToken'] !== undefined &&
            headers['cookieIdentityToken'] !== '' &&
            headers['cookieIdentityToken'] !== null
        ) {
            headers['identityToken'] = headers['cookieIdentityToken'];
            delete headers['cookieIdentityToken'];
        }

        let options = {
            cache: 'no-cache',
            headers: headers,
            method: method,
        };
        Object.keys(requestOptions).forEach((key) => {
            if (['mode', 'cache', 'credentials', 'redirect', 'referrerPolicy'].includes(key)) {
                options[key] = requestOptions[key];
                delete requestOptions[key];
            }
        });
        if (data !== null) {
            options.body = JSON.stringify(data);
        }

        return new Promise((resolve, reject) => {
            fetch(url, options)
                .then((res) => {
                    res.text().then((respText) => {
                        // if ((url.includes("recommend") || url.includes("actions"))) {
                        //     track && track("TEST_TA_MIXPANEL_EVENTS", {
                        //         URL: url,
                        //         fetchOptions: JSON.stringify({options}),
                        //         from: "then",
                        //         hasError: respText.includes("error"),
                        //         response: respText,
                        //         responseStatus: res.status,
                        //         responseStatusText: res.statusText
                        //     });
                        // }
                        let parsedRes = {};
                        let parseFailure = false;
                        try {
                            parsedRes = JSON.parse(respText);
                        } catch {
                            parseFailure = true;
                        }

                        if (responseStatus) {
                            const respJson = respText.length ? parsedRes : {};
                            resolve(respJson);
                        }

                        if (respText !== '' && typeof parsedRes.error !== 'undefined') {
                            forceLogout(parsedRes.error?.code); // check for code and force logout
                        }

                        if (res.status >= 200 && res.status < 300) {
                            // Detect decoding failure: non-empty body that could not be parsed as JSON
                            if (parseFailure && respText.length > 0 && requestOptions && requestOptions.responseJson) {
                                reject({
                                    data: null,
                                    raw: respText,
                                    networkError: {
                                        errorType: ErrorType.RESPONSE_DECODING_FAILURE,
                                        url,
                                        statusCode: res.status,
                                    },
                                });
                                return true;
                            }

                            if (requestOptions && requestOptions.responseJson) {
                                const responseJson = respText.length ? parsedRes : {};
                                if (!responseJson) {
                                    reject({ code: 500 });
                                    return true;
                                }
                                resolve(responseJson);
                                return true;
                            } else if (requestOptions && requestOptions.responseJson === false) {
                                const responseXml = SMILHandler.parseXML(respText);
                                if (
                                    typeof responseXml.error !== 'undefined' &&
                                    responseXml.error.responseCode === '403'
                                ) {
                                    users.refreshUserToken(false, true);
                                }
                            }
                            resolve(respText);
                            return true;
                        } else if (res.status === 403) {
                            users.refreshUserToken(false, true);
                        }

                        // 4xx / 5xx — API_FAILURE
                        reject({
                            data: parsedRes?.error || null,
                            raw: respText,
                            networkError: {
                                errorType: ErrorType.API_FAILURE,
                                url,
                                statusCode: res.status,
                                errorData: parsedRes?.error || {},
                            },
                        });
                    });
                })
                .catch((error) => {
                    // if (url.includes("recommend") || url.includes("actions")) {
                    //     track && track("TEST_TA_MIXPANEL_EVENTS", {
                    //         URL: url,
                    //         fetchOptions: JSON.stringify({options}),
                    //         from: "catch",
                    //         error: JSON.stringify({error})
                    //     });
                    // }
                    const errorType = classifyNetworkError(error);
                    error.networkError = {
                        errorType,
                        url,
                        networkErrorMessage: error.message,
                    };
                    reject(error);
                });
        });
    },
    jsonP(url, callback, param = 'callback') {
        try {
            const script = document.createElement('script');
            const functionName = 'callback_' + Math.random().toString(36).substring(7);
            window[functionName] = callback;
            script.src = url + '?' + param + '=' + functionName;
            window.document.querySelector('body').appendChild(script);
        } catch (err) {
            callback({ error: true, details: err });
        }
    },
    filterPageContent: (pagesContents, pageKeyP, pageIdP) => {
        let contentFilter = null;
        if (pagesContents !== null && pagesContents.length > 0) {
            contentFilter = pagesContents.filter(({ pageKey, pageId }) => {
                return pageKey === pageKeyP && pageId === pageIdP;
            });
        }
        return contentFilter !== null && contentFilter.length > 0 ? contentFilter[0] : null;
    },

    // /**
    //  *
    //  * @param {*} config
    //  * @returns {CmsServices}
    //  */
    // getCmsService: () => {
    //     // if (apis && apis.cmsService) {
    //     //     return apis.cmsService;
    //     // }
    //     //apis.cmsService = new CmsServices(appConfig.cmsBaseUrl, appConfig.appKey);

    //     // apis.cmsService = new CmsServices(appConfig.baseConfigs);
    //     // return apis.cmsService;
    // },

    // /**
    //  *
    //  * @param {*} config
    //  * @returns {MpxServices}
    //  */
    // getMpxService: () => {
    //     // if (apis && apis.mpxService) {
    //     //     return apis.mpxService;
    //     // }
    //     // const config = get(CACHE_CONFIG_TYPES.configuration);
    //     // apis.mpxService = new MpxServices(config?.apiKeys?.mpx);
    //     // return apis.mpxService;
    // },

    // /**
    //  *
    //  * @param {*} config
    //  * @returns {ThinkAnalyticsServices}
    //  */
    // getThinkAnalyticsService: () => {
    //     // if (apis && apis.thinkAnalyticsService) {
    //     //     return apis.thinkAnalyticsService;
    //     // }
    //     // const context = appData;
    //     // apis.thinkAnalyticsService = new ThinkAnalyticsServices(context.config.api.recommendation);
    //     // return apis.thinkAnalyticsService;
    // },

    // /**
    //  *
    //  * @param {*} config
    //  * @returns {JawwyServices}
    //  */
    // getJawwyService: () => {
    //     // if (apis && apis.jawwyService) {
    //     //     return apis.jawwyService;
    //     // }
    //     // const config = get(CACHE_CONFIG_TYPES.configuration);
    //     // const apikey = config?.apiKeys?.jawwy?.api_key;
    //     // const appKey = config?.apiKeys?.jawwy?.app_key;
    //     // apis.jawwyService = new JawwyServices(config?.apiKeys?.jawwy, apikey, appKey);
    //     // return apis.jawwyService;
    // },

    // /**
    //  *
    //  * @param {*} config
    //  * @returns {RecommendationServices}
    //  */
    // getRecommendationServices: () => {
    //     // if (apis && apis.recommendationServices) {
    //     //     return apis.recommendationServices;
    //     // }
    //     // const config = get(CACHE_CONFIG_TYPES.configuration);
    //     // const apikey = config?.apiKeys?.jawwy?.api_key;
    //     // const appKey = config?.apiKeys?.jawwy?.app_key;
    //     // apis.recommendationServices = new RecommendationServices(config?.api?.recommendation, apikey, appKey);
    //     // return apis.recommendationServices;
    // },

    // /**
    //  *
    //  * @param {*} config
    //  * @returns {BOLTServices}
    //  */
    // getBOLTServices: (config = null) => {
    //     // if (apis && apis.boltService) {
    //     //     return apis.boltService;
    //     // }
    //     // const context = config !== null ? { config: config } : appData;
    //     // if (typeof context.config === 'undefined') return false;
    //     // const apikey = user.getAPIKey() || context.config.api.bolt.api_key;
    //     // const appKey = context.config.api.bolt.bolt_app_key;
    //     // apis.boltService = new BOLTServices(context.config.api.bolt, apikey, appKey);
    //     // return apis.boltService;
    // },
    // getDiveService: () => {
    //     // if (apis && apis.diveService) {
    //     //     return apis.diveService;
    //     // }
    //     // const context = appData;
    //     // apis.diveService = new DiveServices(context.config.api.dive);
    //     // return apis.diveService;
    // },
    // getQitafService: () => {
    //     // if (apis && apis.QitafService) {
    //     //     return apis.QitafService;
    //     // }
    //     // const config = get(CACHE_CONFIG_TYPES.configuration);
    //     // const apikey = user.getAPIKey() || config.apiKeys.jawwy.api_key;
    //     // const appKey = config?.apiKeys?.jawwy?.app_key;
    //     // apis.QitafService = new QitafService(config.apiKeys.jawwy, apikey, appKey);
    //     // return apis.QitafService;
    // },
    // getDiveTrendingService: () => {
    //     // if (apis && apis.diveTrendingService) {
    //     //     return apis.diveTrendingService;
    //     // }
    //     // const context = appData;
    //     // apis.diveTrendingService = new DiveServices(context.config.api.dive_trending);
    //     // return apis.diveTrendingService;
    // },
    // getDiveTopPickService: () => {
    //     // if (apis && apis.diveTopPicksService) {
    //     //     return apis.diveTopPicksService;
    //     // }
    //     // const context = appData;
    //     // apis.diveTopPicksService = new DiveServices(context.config.api.dive_top_picks);
    //     // return apis.diveTopPicksService;
    // },

    // /**
    //  *
    //  * @param {*} config
    //  * @returns {WebService}
    //  */
    // getWebService: (config) => {
    //     // if (apis && apis.webService) {
    //     //     return apis.webService;
    //     // }
    //     // const context = config ? { config: config } : appData;
    //     // apis.webService = new WebService(context.config.api.web);
    //     // return apis.webService;
    // },
    // getDefaultLanguage: async (config) => {
    //     // apis.getCmsService().languages().init(config);
    //     // try {
    //     //     return await apis
    //     //         .getCmsService()
    //     //         .languages()
    //     //         .get(config.defaultLanguage ? config.defaultLanguage : appConfig.defaultLanguage);
    //     // } catch (e) {
    //     //     return {};
    //     // }
    // },
    // getAllLanguages: async (config) => {
    //     // apis.getCmsService().languages().init(config);
    //     // try {
    //     //     return await apis.getCmsService().languages().getAll();
    //     // } catch (e) {
    //     //     return {};
    //     // }
    // },

    // callGeoLocationService: async (api = null) => {
    //     try {
    //         // return await container.resolve < IGeoLocationService > (DI.SERVICES.IGeoLocationService).getMyLocation();
    //         return await container.resolve(DI.SERVICES.IGeoLocationService).getMyLocation();
    //     } catch (e) {
    //         return {};
    //     }
    // },
    // getUncachedBOLTServices: (config) => {
    //     // const context = config ? { config: config } : null;
    //     // if (context === null || typeof context.config === 'undefined') return false;
    //     // const apikey = '';
    //     // const appKey = context.config.api.bolt.bolt_app_key;
    //     // return new BOLTServices(context.config.api.bolt, apikey, appKey);
    // },
    /**  @returns {string} */
    get BoltAppKey() {
        const context = appData;
        if (typeof context.config === 'undefined') return '';
        return context.config.apiKeys.bolt.api_key;
    },
};

export default apis;
